// The one span form with units (R2741): `45s` under a minute, `3m 23s`
// under an hour, `3h 23m` from one hour on and `2d 4h` from two days on.
// The unit letters are the four `dur_*` sentences of `[ui.state.text]`, so
// a language writes its own. `state.ts` `dur` and every countdown drawn on
// the map write through it (ui-map-interface.md §17.1 "The countdowns of
// the map").

import { state as S } from '../gen/strings';
import { SEC_PER_HOUR, SEC_PER_MINUTE, wholeSec } from './clock';

/** From this many hours on, a span reads in days and hours. */
const DAY_FORM_HOURS = 48;

/**
 * A span of seconds with its units. It floors to the whole second; a
 * figure under 0 or not finite reads `0s`.
 */
export function spanText(sec: number): string {
  const s = wholeSec(sec);
  if (s < SEC_PER_MINUTE) return S.dur_seconds({ sec: s });
  if (s < SEC_PER_HOUR) {
    return S.dur_minutes({ min: Math.floor(s / SEC_PER_MINUTE), sec: s % SEC_PER_MINUTE });
  }
  const h = Math.floor(s / SEC_PER_HOUR);
  const m = Math.floor((s % SEC_PER_HOUR) / SEC_PER_MINUTE);
  if (h < DAY_FORM_HOURS) return S.dur_hours({ hours: h, min: m });
  return S.dur_days({ days: Math.floor(h / 24), hours: h % 24 });
}

/**
 * The step a countdown's words move by: the whole second under an hour,
 * the whole minute from one hour on. The count rounds up, so a countdown
 * never reads `0s` before its instant. A layer that caches a countdown
 * writes it again when this key moves, and on nothing finer.
 */
export function leftKey(sec: number): number {
  const s = Math.ceil(isFinite(sec) ? Math.max(0, sec) : 0);
  return s < SEC_PER_HOUR ? s : s - (s % SEC_PER_MINUTE);
}

/** A countdown on the map, with its units: `spanText` of `leftKey`. */
export function leftSpan(sec: number): string {
  return spanText(leftKey(sec));
}

/** Does a countdown of `sec` move once a second? Under an hour it does,
 *  so a piece that packs it once must pack again on every world second. */
export function leftTicksBySecond(sec: number): boolean {
  return leftKey(sec) < SEC_PER_HOUR;
}

/**
 * The letters the four span forms write in the picked language (`smhd` in
 * English), without the digits and the spaces. The figure alphabet of the
 * map bakes them, so an anchoring count can write its units (R2741).
 */
export const SPAN_LETTERS: string = (() => {
  const all = S.dur_seconds({ sec: 0 }) + S.dur_minutes({ min: 0, sec: 0 })
    + S.dur_hours({ hours: 0, min: 0 }) + S.dur_days({ days: 0, hours: 0 });
  return [...new Set(all.replace(/[\d\s]/g, ''))].join('');
})();
